import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Warehouse, Scale, Package, IndianRupee, Check, Gavel, Undo2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import {
  AggHeader, PeriodSelect, ResetSampleButton, Kpi, Panel, useSort, SortTh, TH_ROW, TD, inr, num, pct, fmtDate,
  CHART_COLORS, Pill, TableFilter, textMatch, FieldSelect,
} from "./aggKit";

const RES_LABEL = { accept_carrier: "Carrier wt accepted", accept_counter: "Counter wt accepted", disputed: "Disputed with 3PL" };
const RES_STYLE = {
  accept_carrier: "bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30",
  accept_counter: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  disputed: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
};

export default function AggFranchisePage() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const [fq, setFq] = useState("");
  const [fState, setFState] = useState("open");
  const [fHub, setFHub] = useState("");

  const load = useCallback(() => {
    api.get("/agg/franchise", { params: { days } }).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [days]);
  useEffect(() => { load(); }, [load, tick]);

  const hubSort = useSort(d?.hubs || [], "parcels");
  const flaggedRows = useMemo(() => (d?.flagged || []).filter((f) =>
    (fState === "all" || (fState === "open" ? !f.resolution : !!f.resolution)) &&
    (!fHub || f.hub_id === fHub) && textMatch(f, fq, ["awb", "order_id", "merchant_name", "carrier_name", "hub_name"])), [d, fState, fHub, fq]);
  const flagSort = useSort(flaggedRows, "diff_pct");

  const resolve = async (f, resolution) => {
    let note = "";
    if (resolution === "disputed") {
      note = window.prompt(`Raise a weight dispute with ${f.carrier_name} for ${f.awb}.\nNote for the carrier (optional):`, `Counter weight ${f.weight_hub_kg} kg verified at hub`);
      if (note === null) return;
    }
    try {
      await api.post(`/agg/shipments/${f.id}/weight-resolution`, { resolution, note });
      toast.success(resolution === "clear" ? "Resolution cleared" : RES_LABEL[resolution]);
      setTick((t) => t + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const t = d?.totals;
  const avgVar = d?.hubs?.length ? d.hubs.reduce((a, h) => a + h.variance_pct * h.parcels, 0) / Math.max(1, t.parcels) : 0;

  return (
    <div className="space-y-4" data-testid="agg-franchise-page">
      <AggHeader icon={Warehouse} title="First-Mile Franchise Hubs"
        subtitle="How many parcels each franchise hub takes in, and where the counter weight differs from the carrier's manifest weight."
        actions={<><PeriodSelect value={days} onChange={setDays} testid="fr-period" /><ResetSampleButton onDone={() => setTick((x) => x + 1)} /></>} />
      {!d ? <div className="text-sm text-muted-foreground p-6">Loading hubs…</div> : (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            <Kpi testid="fr-kpi-intake" label="Parcel intake" icon={Package} value={num(t.parcels)} sub={`across ${d.hubs.length} franchise hubs`} />
            <Kpi testid="fr-kpi-flags" label="Weight mismatch flags" icon={Scale} accent="text-violet-500" value={num(t.flags)} sub={`${num(t.open_flags)} still open · tolerance ±10%`} />
            <Kpi testid="fr-kpi-recover" label="Overcharge at risk" icon={IndianRupee} accent="text-rose-500" value={inr(t.recoverable)} sub="extra kg × carrier rate on open flags" />
            <Kpi testid="fr-kpi-var" label="Weighted weight variance" icon={Scale} accent="text-sky-500" value={pct(avgVar)} sub="carrier manifest vs hub counter" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-3">
            <Panel className="xl:col-span-3" testid="hub-table" title="Intake by franchise hub" subtitle="Click a hub to open its parcels" bodyClass="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className={TH_ROW}>
                  <tr>
                    <SortTh k="hub_name" label="Hub" sort={hubSort} />
                    <SortTh k="today" label="Today" sort={hubSort} align="right" />
                    <SortTh k="parcels" label="Parcels" sort={hubSort} align="right" />
                    <SortTh k="utilisation_pct" label="Capacity used" sort={hubSort} />
                    <SortTh k="kg_counter" label="Counter kg" sort={hubSort} align="right" />
                    <SortTh k="kg_carrier" label="3PL kg" sort={hubSort} align="right" />
                    <SortTh k="variance_pct" label="Var %" sort={hubSort} align="right" />
                    <SortTh k="flags" label="Flags" sort={hubSort} align="right" />
                    <SortTh k="commission" label="Commission" sort={hubSort} align="right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {hubSort.sorted.map((h) => (
                    <tr key={h.hub_id} className="hover:bg-muted/40" data-testid={`hub-row-${h.code}`}>
                      <td className={TD}><Link to={`/dashboard/agg/shipments?hub_id=${h.hub_id}`} className="font-semibold hover:text-primary">{h.hub_name}</Link><div className="text-[10px] text-muted-foreground">{h.franchisee_name} · {h.code}</div></td>
                      <td className={`${TD} text-right font-mono-num`}>{h.today}</td>
                      <td className={`${TD} text-right font-mono-num font-bold`}>{num(h.parcels)}</td>
                      <td className={TD}>
                        <div className="flex items-center gap-2 min-w-[110px]">
                          <div className="flex-1 h-1.5 bg-muted"><div className={`h-full ${h.utilisation_pct > 90 ? "bg-rose-500" : h.utilisation_pct > 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, h.utilisation_pct)}%` }} /></div>
                          <span className="font-mono-num text-[10px] w-14 text-right">{h.avg_daily}/{h.daily_capacity}</span>
                        </div>
                      </td>
                      <td className={`${TD} text-right font-mono-num`}>{num(h.kg_counter, 1)}</td>
                      <td className={`${TD} text-right font-mono-num`}>{num(h.kg_carrier, 1)}</td>
                      <td className={`${TD} text-right font-mono-num ${Math.abs(h.variance_pct) > 5 ? "text-violet-600 dark:text-violet-400 font-bold" : ""}`}>{h.variance_pct > 0 ? "+" : ""}{h.variance_pct}%</td>
                      <td className={`${TD} text-right font-mono-num`}>{h.flags ? <span className="text-violet-600 dark:text-violet-400 font-bold">{h.flags}</span> : "—"}<span className="text-[10px] text-muted-foreground"> ({pct(h.flag_rate_pct, 0)})</span></td>
                      <td className={`${TD} text-right font-mono-num`}>{inr(h.commission)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
            <Panel className="xl:col-span-2" testid="hub-trend" title="Daily intake by hub" bodyClass="p-3">
              <div className="h-[290px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.trend} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} minTickGap={18} />
                    <YAxis tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0B1220", border: "1px solid #26324d", fontSize: 11, color: "#fff" }} labelFormatter={(v) => fmtDate(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {d.hub_codes.map((c, i) => <Bar key={c} dataKey={c} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>

          <Panel testid="weight-flags" title="Weight discrepancy flags" subtitle="Franchise counter weight vs final carrier manifest weight (more than ±10% and at least 0.25 kg apart)"
            actions={<>
              <FieldSelect testid="wf-state" value={fState} onChange={setFState} className="h-8 text-xs"><option value="open">Open</option><option value="resolved">Resolved</option><option value="all">All</option></FieldSelect>
              <FieldSelect testid="wf-hub" value={fHub} onChange={setFHub} className="h-8 text-xs"><option value="">All hubs</option>{d.hubs.map((h) => <option key={h.hub_id} value={h.hub_id}>{h.hub_name}</option>)}</FieldSelect>
              <TableFilter testid="wf-search" value={fq} onChange={setFq} placeholder="Filter AWB, merchant…" />
            </>} bodyClass="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className={TH_ROW}>
                <tr>
                  <SortTh k="awb" label="AWB" sort={flagSort} />
                  <SortTh k="manifested_at" label="Date" sort={flagSort} />
                  <SortTh k="hub_name" label="Hub" sort={flagSort} />
                  <SortTh k="merchant_name" label="Merchant" sort={flagSort} />
                  <SortTh k="carrier_name" label="Carrier" sort={flagSort} />
                  <SortTh k="weight_hub_kg" label="Counter" sort={flagSort} align="right" />
                  <SortTh k="weight_carrier_kg" label="3PL" sort={flagSort} align="right" />
                  <SortTh k="diff_pct" label="Diff" sort={flagSort} align="right" />
                  <SortTh k="cost_impact" label="₹ impact" sort={flagSort} align="right" />
                  <th className="px-3 py-2 text-left">Resolution</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {flagSort.sorted.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-muted-foreground">No flags here.</td></tr>}
                {flagSort.sorted.map((f) => (
                  <tr key={f.id} className="hover:bg-muted/40" data-testid={`wf-row-${f.id}`}>
                    <td className={`${TD} font-mono-num font-bold`}>{f.awb}</td>
                    <td className={TD}>{fmtDate(f.manifested_at)}</td>
                    <td className={TD}>{f.hub_name}</td>
                    <td className={`${TD} max-w-[150px] truncate`}>{f.merchant_name}</td>
                    <td className={TD}>{f.carrier_name}</td>
                    <td className={`${TD} text-right font-mono-num`}>{f.weight_hub_kg} kg</td>
                    <td className={`${TD} text-right font-mono-num`}>{f.weight_carrier_kg} kg</td>
                    <td className={`${TD} text-right font-mono-num font-bold ${f.diff_kg > 0 ? "text-rose-600 dark:text-rose-400" : "text-sky-600 dark:text-sky-400"}`}>{f.diff_kg > 0 ? "+" : ""}{f.diff_kg} kg ({f.diff_pct > 0 ? "+" : ""}{f.diff_pct}%)</td>
                    <td className={`${TD} text-right font-mono-num`}>{inr(f.cost_impact, 2)}</td>
                    <td className={TD}>
                      {f.resolution ? (
                        <div className="flex items-center gap-1.5">
                          <Pill className={RES_STYLE[f.resolution.resolution]}>{RES_LABEL[f.resolution.resolution]}</Pill>
                          <button title="Re-open" onClick={() => resolve(f, "clear")} data-testid={`wf-clear-${f.id}`} className="h-6 w-6 grid place-items-center border border-border hover:bg-muted"><Undo2 className="h-3 w-3" /></button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <button onClick={() => resolve(f, "accept_carrier")} data-testid={`wf-accept-carrier-${f.id}`} className="h-6 px-1.5 border border-border hover:bg-muted text-[10px] font-bold uppercase inline-flex items-center gap-1"><Check className="h-3 w-3" /> 3PL wt</button>
                          <button onClick={() => resolve(f, "accept_counter")} data-testid={`wf-accept-counter-${f.id}`} className="h-6 px-1.5 border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10 text-[10px] font-bold uppercase inline-flex items-center gap-1"><Check className="h-3 w-3" /> Counter wt</button>
                          <button onClick={() => resolve(f, "disputed")} data-testid={`wf-dispute-${f.id}`} className="h-6 px-1.5 border border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10 text-[10px] font-bold uppercase inline-flex items-center gap-1"><Gavel className="h-3 w-3" /> Dispute</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </div>
  );
}
